@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginecardview.layouts

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal const val WA_PLATZ_TYP = "WAPlatz"

internal object WAPlatzUebersichtStyles : StyleSheet() {
    val card by style {
        width(360.px)
        height(405.px)
        padding(12.px)
        borderRadius(20.px)
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}")
        backgroundColor(DashboardTheme.SURFACE_CARD)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
        overflow("hidden")
    }
    val free by style {
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
    }
    val occupied by style {
        property("border", "1px solid ${UISettings.ACCENT}")
        property("box-shadow", "0 14px 28px ${DashboardTheme.SURFACE_SHADOW}, 0 0 20px rgba(255,0,0,0.16)")
    }
    val header by style {
        width(100.percent)
        minHeight(72.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val code by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(30.px)
        fontWeight("800")
        lineHeight("1.15")
        textAlign("center")
        property("word-break", "break-word")
    }
    val preview by style {
        width(100.percent)
        height(204.px)
        position(Position.Relative)
        flexShrink(0)
        marginTop(4.px)
        borderRadius(16.px)
        property("overflow", "hidden")
        backgroundColor(Color.transparent)
    }
    val previewContent by style {
        position(Position.Absolute)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        padding(16.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        gap(10.px)
        overflow("hidden")
    }
    val entryItem by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }
    val headline by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(19.px)
        fontWeight("800")
        lineHeight("1.2")
    }
    val meta by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(7.px)
        color(DashboardTheme.TEXT_MUTED)
        fontSize(16.px)
        lineHeight("1.25")
    }
    val metaRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val metaValue by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontWeight("700")
        fontSize(16.px)
        textAlign("right")
    }
    val bottom by style {
        width(100.percent)
        flexGrow(1)
        minHeight(0.px)
        padding(4.px, 12.px, 10.px, 12.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.SpaceBetween)
        gap(6.px)
        boxSizing("border-box")
    }
    val metaLine by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
        paddingBottom(10.px)
        overflow("hidden")
    }
    val metaText by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(17.px)
        fontWeight("700")
        property("white-space", "nowrap")
    }
    val centerText by style {
        color(DashboardTheme.TEXT_MUTED)
    }
}

@Composable
internal fun WAPlatzCard(platz: All, waAuftrag: All?) {
    val occupied = waAuftrag != null
    val code = platz.platzcode.ifBlank { platz.id }
    val halle = platz.halle.ifBlank { "-" }
    val paletten = waAuftrag?.palettenWerte ?: emptyMap()
    val gesamt = paletten.values.sumOf { it.toIntOrNull() ?: 0 }

    Div({
        classes(WAPlatzUebersichtStyles.card)
        classes(if (occupied) WAPlatzUebersichtStyles.occupied else WAPlatzUebersichtStyles.free)
    }) {
        Div({ classes(WAPlatzUebersichtStyles.header) }) {
            Span({ classes(WAPlatzUebersichtStyles.code) }) { Text("WA-$code") }
        }
        Div({ classes(WAPlatzUebersichtStyles.preview) }) {
            if (occupied) {
                Div({ classes(WAPlatzUebersichtStyles.previewContent) }) {
                    Div({ classes(WAPlatzUebersichtStyles.entryItem) }) {
                        Div({ classes(WAPlatzUebersichtStyles.headline) }) {
                            Span { Text("LS: ${waAuftrag.lieferscheinnummer.ifBlank { "-" }}") }
                            Span { Text("WA") }
                        }
                        Div({ classes(WAPlatzUebersichtStyles.meta) }) {
                            WAMetaRow("Empfänger", waAuftrag.empfaenger.ifBlank { "-" })
                            WAMetaRow("Tor", waAuftrag.tor?.toString().orEmpty().ifBlank { "-" })
                            paletten.forEach { (typ, wert) ->
                                val n = wert.toIntOrNull() ?: 0
                                if (n > 0) WAMetaRow(typ, n.toString())
                            }
                            WAMetaRow("Gesamt", "$gesamt Pal.")
                        }
                    }
                }
            }
        }
        Div({ classes(WAPlatzUebersichtStyles.bottom) }) {
            Div({ classes(WAPlatzUebersichtStyles.metaLine) }) {
                Span({ classes(WAPlatzUebersichtStyles.metaText) }) { Text("Halle $halle") }
                code.takeIf { it.isNotBlank() }?.let {
                    Span({ classes(WAPlatzUebersichtStyles.metaText) }) { Text("|") }
                    Span({ classes(WAPlatzUebersichtStyles.metaText) }) { Text(it) }
                }
            }
        }
    }
}

@Composable
private fun WAMetaRow(label: String, value: String) {
    Div({ classes(WAPlatzUebersichtStyles.metaRow) }) {
        Span { Text(label) }
        Span({ classes(WAPlatzUebersichtStyles.metaValue) }) { Text(value) }
    }
}

@Composable
internal fun WACenterText(text: String) {
    Div({
        classes(WAPlatzUebersichtStyles.centerText)
        style {
            flex(1)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
    }) { Text(text) }
}
